﻿package com.mgacreative.hotelru.feature.reports.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.realtime.realtime
import io.github.jan.supabase.realtime.PostgresAction
import io.github.jan.supabase.realtime.channel
import io.github.jan.supabase.realtime.postgresChangeFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.minus

import com.mgacreative.hotelru.domain.model.AgencyAllotment
import com.mgacreative.hotelru.domain.model.Reservation

data class DashboardState(
    val occupancyPercent: Int = 0,
    val todaysCheckIns: Int = 0,
    val todaysCheckOuts: Int = 0,
    val availableRooms: Int = 0,
    val roomsInMaintenance: Int = 0,
    val occupiedRooms: Int = 0,
    val todaysRevenue: Double = 0.0,
    val pendingPayments: Double = 0.0,
    val totalAgencyAllotments: Int = 0,
    val agencyAllotmentDetails: List<AgencyAllotment> = emptyList(),
    val weeklyData: List<Float> = emptyList(),
    val weeklyLabels: List<String> = emptyList(),
    val monthlyData: List<Float> = emptyList(),
    val monthlyLabels: List<String> = emptyList(),
    val yearlyData: List<Float> = emptyList(),
    val yearlyLabels: List<String> = emptyList(),
    val isLoading: Boolean = true
)

class DashboardViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _state = MutableStateFlow(DashboardState())
    val state: StateFlow<DashboardState> = _state.asStateFlow()

    fun loadDashboardData() {
        viewModelScope.launch {
            _state.value = _state.value.copy(isLoading = true)
            fetchAggregatedData()
            setupRealtimeSubscriptions()
        }
    }

    private suspend fun fetchAggregatedData() {
        try {
            val todayDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
            val tenantId = tenantSession.currentTenantId

            if (tenantId == null) {
                _state.value = _state.value.copy(
                    isLoading = false
                )
                return
            }

            // Fetch all rooms
            val roomsResponse = supabaseClient.client.postgrest["rooms"].select().decodeList<com.mgacreative.hotelru.domain.model.Room>()
            val available = roomsResponse.count { it.status == "CLEAN" || it.status == "AVAILABLE" }
            val inMaintenance = roomsResponse.count { it.status == "MAINTENANCE" }
            val occupied = roomsResponse.count { it.status == "OCCUPIED" }
            val totalRooms = roomsResponse.size
            val occPercent = if (totalRooms > 0) ((occupied.toFloat() / totalRooms) * 100).toInt() else 0

            // Fetch today's check-ins
            val checkInsResponse = supabaseClient.client.postgrest["reservations"].select {
                filter {
                    eq("check_in_date", todayDate)
                }
            }.decodeList<com.mgacreative.hotelru.domain.model.Reservation>()
            
            // Beklenen girişler: Henüz CHECKED_IN veya CANCELLED olmayanlar
            val expectedCheckIns = checkInsResponse.count { it.status == "CONFIRMED" || it.status == "PENDING" }
            
            // Fetch today's check-outs
            val checkOutsResponse = supabaseClient.client.postgrest["reservations"].select {
                filter {
                    eq("check_out_date", todayDate)
                }
            }.decodeList<com.mgacreative.hotelru.domain.model.Reservation>()
            
            // Beklenen çıkışlar: Sadece şu an CHECKED_IN olanlar
            val expectedCheckOuts = checkOutsResponse.count { it.status == "CHECKED_IN" }
            
            // Fetch agency allotments for today
            val allotmentsResponse = supabaseClient.client.postgrest["agency_allotments"].select(columns = io.github.jan.supabase.postgrest.query.Columns.raw("*, agencies(*)")) {
                filter {
                    lte("start_date", todayDate)
                    gte("end_date", todayDate)
                }
            }.decodeList<AgencyAllotment>()
            
            val totalAllotments = allotmentsResponse.sumOf { it.allottedRooms }
            val adjustedAvailable = (available - totalAllotments).coerceAtLeast(0)

            // --- CHART DATA CALCULATIONS ---
            val allReservations = supabaseClient.client.postgrest["reservations"].select().decodeList<Reservation>()
            
            val todayDateObj = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
            
            // Helper function
            fun calcOccupancy(dateStr: String): Float {
                if (totalRooms == 0) return 0f
                val occ = allReservations.count {
                    it.status != "CANCELLED" && it.checkInDate <= dateStr && it.checkOutDate > dateStr
                }
                return (occ.toFloat() / totalRooms) * 100f
            }

            // Weekly (Past 7 days)
            val weeklyDates = (6 downTo 0).map { todayDateObj.minus(DatePeriod(days = it)) }
            val wData = weeklyDates.map { calcOccupancy(it.toString()) }
            val wLabels = weeklyDates.map { "${it.dayOfMonth}/${it.monthNumber}" }

            // Monthly (Past 5 weeks to represent a month span)
            val monthlyDates = (4 downTo 0).map { todayDateObj.minus(DatePeriod(days = it * 7)) }
            val mData = monthlyDates.map { calcOccupancy(it.toString()) }
            val mLabels = monthlyDates.map { "${it.dayOfMonth}/${it.monthNumber}" }

            // Yearly (Past 12 months)
            val yearlyDates = (11 downTo 0).map { todayDateObj.minus(DatePeriod(months = it)) }
            val yData = yearlyDates.map { calcOccupancy(it.toString()) }
            val yLabels = yearlyDates.map { "${it.monthNumber}/${it.year.toString().takeLast(2)}" }

            _state.value = _state.value.copy(
                occupancyPercent = occPercent,
                availableRooms = adjustedAvailable,
                roomsInMaintenance = inMaintenance,
                occupiedRooms = occupied,
                todaysCheckIns = expectedCheckIns,
                todaysCheckOuts = expectedCheckOuts,
                totalAgencyAllotments = totalAllotments,
                agencyAllotmentDetails = allotmentsResponse,
                weeklyData = wData,
                weeklyLabels = wLabels,
                monthlyData = mData,
                monthlyLabels = mLabels,
                yearlyData = yData,
                yearlyLabels = yLabels,
                isLoading = false
            )
        } catch (e: Exception) {
            _state.value = _state.value.copy(isLoading = false)
        }
    }

    private fun setupRealtimeSubscriptions() {
        val channel = supabaseClient.client.channel("public-db-changes")

        // Listen for ANY changes in reservations
        val reservationsFlow = channel.postgresChangeFlow<PostgresAction>(schema = "public") {
            table = "reservations"
        }

        // Listen for ANY changes in payments
        val paymentsFlow = channel.postgresChangeFlow<PostgresAction>(schema = "public") {
            table = "payments"
        }
        
        // Listen for ANY changes in rooms
        val roomsFlow = channel.postgresChangeFlow<PostgresAction>(schema = "public") {
            table = "rooms"
        }

        reservationsFlow.onEach {
            // When a reservation changes, refetch stats
            fetchAggregatedData()
        }.launchIn(viewModelScope)

        paymentsFlow.onEach {
            fetchAggregatedData()
        }.launchIn(viewModelScope)
        
        roomsFlow.onEach {
            fetchAggregatedData()
        }.launchIn(viewModelScope)

        viewModelScope.launch {
            try {
                supabaseClient.client.realtime.connect()
                channel.subscribe()
            } catch (e: Exception) {
                println("Dashboard Realtime note: ${e.message}")
            }
        }
    }

    override fun onCleared() {
        super.onCleared()
        viewModelScope.launch {
            try {
                supabaseClient.client.realtime.disconnect()
            } catch (_: Exception) {}
        }
    }
}


